import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';

// ============================================================================
// Smart Photo Selection
// ============================================================================

const CARD_KEYWORDS: Record<string, string[]> = {
  floorplans: ['interior', 'apartment', 'unit', 'bedroom', 'living room', 'kitchen', 'bathroom', 'floor plan', 'model', 'home', 'layout', 'spacious'],
  gallery: ['community', 'building', 'property', 'exterior', 'entrance', 'lobby', 'leasing', 'welcome', 'amenity', 'pool', 'lounge'],
  amenities: ['pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'recreation', 'courtyard', 'grill', 'playground', 'spa', 'theater'],
};

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const searchText = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!searchText) return 0;
  return keywords.reduce((score, kw) => score + (searchText.includes(kw) ? 1 : 0), 0);
}

function findBestPhotoIndex(
  gallery: PhotoGallery[],
  cardKey: string,
  usedIndices: Set<number>,
  fallbackIndex: number,
): number {
  const keywords = CARD_KEYWORDS[cardKey] || [];
  if (!gallery.length || !keywords.length) return fallbackIndex;

  let bestIndex = -1;
  let bestScore = 0;

  for (let i = 0; i < gallery.length; i++) {
    if (usedIndices.has(i)) continue;
    const score = scorePhoto(gallery[i], keywords);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }

  if (bestIndex >= 0) return bestIndex;

  for (let i = fallbackIndex; i < gallery.length; i++) {
    if (!usedIndices.has(i)) return i;
  }
  for (let i = 0; i < fallbackIndex; i++) {
    if (!usedIndices.has(i)) return i;
  }

  return Math.min(fallbackIndex, Math.max(gallery.length - 1, 0));
}

function useSmartPhotoIndices(): [number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const used = new Set<number>();
    const keys = ['floorplans', 'gallery', 'amenities'] as const;
    const fallbacks = [1, 3, 5];
    const indices: number[] = [];
    for (let i = 0; i < keys.length; i++) {
      const idx = findBestPhotoIndex(gallery, keys[i], used, fallbacks[i]);
      used.add(idx);
      indices.push(idx);
    }
    return indices as [number, number, number];
  }, [gallery]);
}

function makeSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Static slot group definitions (for contentSchema)
// ============================================================================

const card1ImageDef: SlotGroupDefinition = {
  label: 'Floor Plans Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Gallery Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Amenities Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card1LinkDef: SlotGroupDefinition = {
  label: 'Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Choose Your Floorplan' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card2LinkDef: SlotGroupDefinition = {
  label: 'Gallery Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Browse The Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card3LinkDef: SlotGroupDefinition = {
  label: 'Amenities Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View The Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// CTA Card Sub-component
// ============================================================================

function CTACard({
  imageUrl,
  imageAlt,
  titleNode,
  descNode,
  linkNode,
  glowDelay = 0.3,
}: {
  imageUrl?: string;
  imageAlt?: string;
  titleNode: React.ReactNode;
  descNode: React.ReactNode;
  linkNode: React.ReactNode;
  glowDelay?: number;
}) {
  const imgCls = 'absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105';
  return (
    <div className="cta-card glow-trace-wrap relative overflow-hidden group">
      <GlowTrace delay={glowDelay} />
      {imageUrl ? (
        <VibesitesImage url={imageUrl} alt={imageAlt || ''} className={imgCls} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" />
      ) : (
        <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
      )}
      <div className="absolute inset-0 cta-card-overlay" />
      <div className="relative z-10 flex flex-col justify-end h-full p-5 sm:p-6 lg:p-7">
        {titleNode}
        {descNode}
        {linkNode}
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function CTASection() {
  const [idx1, idx2, idx3] = useSmartPhotoIndices();

  const def1 = useMemo(() => makeSlotDef('Floor Plans Image', idx1), [idx1]);
  const def2 = useMemo(() => makeSlotDef('Gallery Image', idx2), [idx2]);
  const def3 = useMemo(() => makeSlotDef('Amenities Image', idx3), [idx3]);

  const img1 = useSlotGroup('ctasection.card1_image', def1);
  const img2 = useSlotGroup('ctasection.card2_image', def2);
  const img3 = useSlotGroup('ctasection.card3_image', def3);

  const link1 = useSlotGroup('ctasection.card1_link', card1LinkDef);
  const link2 = useSlotGroup('ctasection.card2_link', card2LinkDef);
  const link3 = useSlotGroup('ctasection.card3_link', card3LinkDef);

  useRevealStyles();
  const gridRef = useScrollReveal<HTMLDivElement>({ threshold: 0.3 }) as React.RefObject<HTMLDivElement>;

  const titleCls = 'font-heading text-sm sm:text-base font-bold uppercase tracking-[0.15em] text-white mb-2 drop-shadow-lg';
  const btnCls = 'cta-card-btn inline-flex items-center justify-center self-start';

  return (
    <section className="w-full bg-white">
      <div ref={gridRef} className="reveal-fade-up grid grid-cols-1 md:grid-cols-3 gap-2 p-2">
        <EditableSlotGroup groupPath="ctasection.card1_image" definition={def1}>
          <CTACard
            imageUrl={img1.image ?? undefined}
            imageAlt={img1.alt ?? undefined}
            titleNode={
              <RichTextContent path="ctasection.card1.title" label="Card 1 Title" defaultValue="<p>Layouts</p>" as="h2" className={titleCls} />
            }
            descNode={
              <RichTextContent path="ctasection.card1.description" label="Card 1 Description" defaultValue="<p>Choose from our spacious bedroom floor plans.</p>" as="p" className="font-body text-xs sm:text-sm text-white mb-4 leading-relaxed drop-shadow-md line-clamp-2" />
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card1_link" definition={card1LinkDef}>
                <a href={link1.url || '#'} target={link1._target || '_self'} className={`${btnCls} cta-card-btn--card1`}>
                  {link1.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.3}
          />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="ctasection.card2_image" definition={def2}>
          <CTACard
            imageUrl={img2.image ?? undefined}
            imageAlt={img2.alt ?? undefined}
            titleNode={
              <RichTextContent path="ctasection.card2.title" label="Card 2 Title" defaultValue="<p>Picture Yourself Here</p>" as="h2" className={titleCls} />
            }
            descNode={
              <RichTextContent path="ctasection.card2.description" label="Card 2 Description" defaultValue="<p>See what life at our community is all about.</p>" as="p" className="font-body text-xs sm:text-sm text-white mb-4 leading-relaxed drop-shadow-md line-clamp-2" />
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card2_link" definition={card2LinkDef}>
                <a href={link2.url || '#'} target={link2._target || '_self'} className={`${btnCls} cta-card-btn--card2`}>
                  {link2.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.6}
          />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="ctasection.card3_image" definition={def3}>
          <CTACard
            imageUrl={img3.image ?? undefined}
            imageAlt={img3.alt ?? undefined}
            titleNode={
              <RichTextContent path="ctasection.card3.title" label="Card 3 Title" defaultValue="<p>Convenience All Around</p>" as="h2" className={titleCls} />
            }
            descNode={
              <RichTextContent path="ctasection.card3.description" label="Card 3 Description" defaultValue="<p>Life is easier at our community.</p>" as="p" className="font-body text-xs sm:text-sm text-white mb-4 leading-relaxed drop-shadow-md line-clamp-2" />
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card3_link" definition={card3LinkDef}>
                <a href={link3.url || '#'} target={link3._target || '_self'} className={`${btnCls} cta-card-btn--card3`}>
                  {link3.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.9}
          />
        </EditableSlotGroup>
      </div>

      <style>{`
        .cta-card {
          height: 28vh;
          min-height: 200px;
          max-height: 320px;
          border-radius: 4px;
        }
        .cta-card-overlay {
          background: linear-gradient(
            to top,
            color-mix(in srgb, var(--vibe-color-primary) 55%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 30%, rgba(0,0,0,0.5)) 40%,
            color-mix(in srgb, var(--vibe-color-primary) 10%, rgba(0,0,0,0.15)) 70%,
            transparent 100%
          );
        }
        .cta-card-btn {
          padding: 0.5rem 1.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
          background: white;
          border-radius: 9999px;
          transition: all 0.3s ease;
          white-space: nowrap;
        }
        .cta-card-btn:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CTASection',
  displayName: 'CTA Cards',
  tokens: {
    'ctasection.card1.title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>Layouts</p>',
    },
    'ctasection.card1.description': {
      label: 'Card 1 Description',
      type: 'richtext',
      defaultValue: '<p>Choose from our spacious bedroom floor plans.</p>',
    },
    'ctasection.card2.title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>Picture Yourself Here</p>',
    },
    'ctasection.card2.description': {
      label: 'Card 2 Description',
      type: 'richtext',
      defaultValue: '<p>See what life at our community is all about.</p>',
    },
    'ctasection.card3.title': {
      label: 'Card 3 Title',
      type: 'richtext',
      defaultValue: '<p>Convenience All Around</p>',
    },
    'ctasection.card3.description': {
      label: 'Card 3 Description',
      type: 'richtext',
      defaultValue: '<p>Life is easier at our community.</p>',
    },
  },
  slotGroups: {
    'ctasection.card1_image': card1ImageDef,
    'ctasection.card2_image': card2ImageDef,
    'ctasection.card3_image': card3ImageDef,
    'ctasection.card1_link': card1LinkDef,
    'ctasection.card2_link': card2LinkDef,
    'ctasection.card3_link': card3LinkDef,
  },
};

(CTASection as any).contentSchema = contentSchema;

export default CTASection;
